import { RootState } from '@/store'
import { CurrentChatSession as CurrentChatSessionType } from '@/store/chat'
import { ChatMessageSender } from '@repo/shared/dtos/message'
import ChatDayMessages from '@repo/ui/chatDayMessages'
import ChatEmpty from '@repo/ui/chatEmpty'
import { DataProvider } from '@repo/ui/dataProvider'
import Message, { isNewThread } from '@repo/ui/message'
import { ScrollArea } from '@repo/ui/scrollArea'
import { cn, createMessagesByDay, sortByKey } from '@repo/ui/utils'
import { Loader2 } from 'lucide-react'
import { useMemo, useRef } from 'react'

export function CurrentChatSession({
  className,
  autoScroll,
}: {
  className?: string
  autoScroll: boolean
}) {
  return (
    <DataProvider
      selectors={[(state: RootState) => state.chat.currentSession]}
      loadingComponent={
        <div className="flex h-full min-h-96 flex-1 items-center justify-center">
          <Loader2 className="text-primary-100 h-20 w-20 animate-spin opacity-30" />
        </div>
      }
      render={(session) => (
        <Session
          autoScroll={autoScroll}
          className={className}
          session={session}
        />
      )}
    />
  )
}

function Session({
  session,
  className,
  autoScroll,
}: {
  session: CurrentChatSessionType
  className?: string
  autoScroll: boolean
}) {
  const m = useMemo(
    () =>
      [...session.messages].sort(sortByKey('sentAt')).map((m) => ({
        content: m.content,
        createdAtTime: m.sentAt,
        id: m.id,
        isSender: m.sender !== ChatMessageSender.Bot,
      })),
    [session.messages],
  )
  const messagesByDay = useMemo(() => createMessagesByDay(m), [m])
  const days = useMemo(() => [...messagesByDay.keys()], [messagesByDay])

  const containerRef = useRef<HTMLDivElement>(null)
  const scrollToLastMessage = () => {
    // FIXME:
    containerRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'end',
    })
  }
  return (
    <div className={cn('flex flex-1 flex-col overflow-hidden', className)}>
      <ScrollArea className="flex flex-col">
        <div className="flex flex-col gap-4 pb-6">
          {days.length > 0 ? (
            <div ref={containerRef} className="flex w-full flex-col">
              {days.map((day, dayIndex) => (
                <ChatDayMessages day={day} key={day}>
                  {messagesByDay.get(day)?.map((m, i) => {
                    return (
                      <Message
                        content={m.content}
                        createdAtTime={m.createdAtTime}
                        isNewThread={isNewThread(m, messagesByDay.get(day)!)}
                        isSender={m.isSender}
                        key={m.id}
                        onLifeCycle={{
                          onMounted: () => {
                            if (
                              autoScroll &&
                              dayIndex === days.length - 1 &&
                              i === messagesByDay.get(day)!.length - 1
                            )
                              scrollToLastMessage()
                          },
                        }}
                      />
                    )
                  })}
                </ChatDayMessages>
              ))}
            </div>
          ) : (
            <ChatEmpty />
          )}
        </div>
      </ScrollArea>
    </div>
  )
}
